import { type v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Notice,
  Toast
} from '@deque/cauldron-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getDeleteConnection } from '~/app/utils/getDeleteConnection';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import { type AuthUser } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { type IntegrationProductInfo } from '~/common/utils/get-integration-product-name-from-slug';
import { type IntegrationConnection } from '~/common/utils/integrations-client/jira/connections';
import styles from '../ConnectionButtonsLine.css';

export interface CustomConnectionButtonsLineProps {
  connection: IntegrationConnection | null;
  integrationProductInfo: IntegrationProductInfo;
  enterprise: v2.Enterprise;
  canManageIntegrations: boolean;
  authUser: AuthUser;
  connectionsUpdated: () => void;
}

export function CustomConnectionButtonsLine({
  connection,
  integrationProductInfo,
  enterprise,
  canManageIntegrations,
  authUser,
  connectionsUpdated
}: CustomConnectionButtonsLineProps) {
  const { t } = useTranslation();

  const history = useHistory();

  const { setContents } = useGlobalToast();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const { displayName } = integrationProductInfo;

  if (isProcessing) {
    return <ScrimmedLoader label={t('Loading...')} />;
  }

  let errorToast = null;
  if (error) {
    errorToast = (
      <Toast show type="error" onDismiss={() => setError('')}>
        {error}
      </Toast>
    );
  }

  let shouldDeleteDialogBox = null;
  if (showAlert) {
    shouldDeleteDialogBox = (
      <Alert
        heading={t('Delete connection')}
        onClose={() => setShowAlert(false)}
        show={showAlert}
        variant="warning"
      >
        <AlertContent>
          {t('Are you sure you want to delete this connection?')}
        </AlertContent>
        <AlertActions>
          <Button
            variant="error"
            onClick={
              getDeleteConnection({
                displayName,
                connection,
                authUser,
                enterprise,
                setIsProcessing,
                setError,
                connectionsUpdated,
                setContents,
                t
              }).deleteConnection
            }
          >
            {t('Delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowAlert(false)}>
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
    );
  }

  if (connection) {
    if (canManageIntegrations) {
      return (
        <>
          {shouldDeleteDialogBox}
          <div className={styles.installedGroup}>
            {errorToast}
            <>
              <Button
                className={styles.darkText}
                variant="secondary"
                onClick={() => {
                  history.push(
                    `/configuration/integrations/${ProductSlugs.customIntegration}/settings/${connection.id}`
                  );
                }}
                aria-label={t(
                  'Modify {{displayName}} connection settings for {{name}}',
                  {
                    displayName,
                    name: connection.name
                  }
                )}
              >
                {t('Modify settings')}
              </Button>
            </>
            <Button
              variant="danger-secondary"
              onClick={() => setShowAlert(true)}
              aria-label={t('Delete {{displayName}} {{name}} connection', {
                displayName,
                name: connection.name
              })}
            >
              {t('Delete connection')}
            </Button>
          </div>
        </>
      );
    } else {
      return (
        <div className={styles.installedGroup}>
          <Notice
            type="info"
            title={t('Settings must be configured by your admin.')}
          />
        </div>
      );
    }
  } else if (canManageIntegrations) {
    return (
      <div className={styles.installedGroup}>
        {errorToast}
        <Button
          variant="primary"
          aria-label={t('Add new connection')}
          onClick={() =>
            history.push(
              `/configuration/integrations/${ProductSlugs.customIntegration}/settings`
            )
          }
        >
          {t('Add new connection to {{displayName}}', { displayName })}
        </Button>
      </div>
    );
  }

  return null;
}
